import { backgroundSecondary } from '@filament/react/atomic-styles';
import { FlexBox } from '@filament/react/layout';
import { NavLink } from '@filament/react/nav-link';
import { type SidebarProps, Sidebar } from '@filament/react/sidebar';
import { H6 } from '@filament/react/text';
import { type SpringConfig, animated, useSpring } from '@react-spring/web';
import { useMatch } from 'react-router-dom';
import { type MenuLeaf, type MenuItem, isSection, menuItems } from './menu';
import * as styles from './SideNavBar.css';

const SideBarMenuLeaf = ({ id, name, href, icon }: MenuLeaf) => {
    const Icon = icon;
    const isActive = !!useMatch(href);
    return (
        <NavLink
            key={id}
            href={href}
            underlineBehavior="none"
            isActive={isActive}>
            {Icon && <Icon />} {name}
        </NavLink>
    );
};

const SideBarMenuItem = (item: MenuItem) => {
    if (isSection(item)) {
        return (
            <>
                <H6 className={styles.readonlyMenuItem}>
                    <span>{item.name}</span>
                </H6>
                {item.children.map(child => <SideBarMenuLeaf key={child.id} {...child} />)}
            </>
        );
    }
    return <SideBarMenuLeaf {...item} />;
};

type SideNavBarProps = SidebarProps & {
    animateConfig: SpringConfig
}

export const SideNavBar = ({ animateConfig, isOpen, ...sidebarProps }: SideNavBarProps) => {
    const animateContent = useSpring({
        config: animateConfig,
        to: {
            transform: isOpen ? 'translateX(0%)' : 'translateX(75%)',
            opacity: isOpen ? 1 : 0,
        },
    });

    return (
        <FlexBox className={backgroundSecondary} height="calc(100vh - 56px)">
            <Sidebar {...sidebarProps} isOpen={isOpen}>
                <animated.div style={animateContent}>
                    {menuItems.map(item => <SideBarMenuItem key={item.id} {...item} />)}
                </animated.div>
            </Sidebar>
        </FlexBox>
    );
};
